import React, { useState } from 'react';
import { 
  User, 
  Users, 
  Crown, 
  Banknote, 
  Settings,
  CheckCircle,
  AlertTriangle,
  XCircle,
  TrendingUp,
  Calendar,
  FileText,
  MessageSquare,
  BarChart3
} from 'lucide-react';

interface ProfileFeature {
  name: string;
  implemented: boolean;
  priority: 'high' | 'medium' | 'low';
  description: string;
  estimatedHours: number;
}

interface ProfileAnalysis {
  name: string;
  icon: React.ComponentType<any>;
  color: string;
  completionRate: number;
  features: ProfileFeature[];
  criticalMissing: string[];
  nextPriorities: string[];
}

const profilesAnalysis: ProfileAnalysis[] = [
  {
    name: 'Porteur de Projet',
    icon: User,
    color: 'green',
    completionRate: 85,
    features: [
      { name: 'Création projet wizard', implemented: true, priority: 'high', description: 'Wizard 12 étapes complet', estimatedHours: 0 },
      { name: 'Upload documents', implemented: true, priority: 'high', description: 'Système complet avec validation', estimatedHours: 0 },
      { name: 'Validation documents', implemented: true, priority: 'high', description: 'Statut validé/rejeté avec notes', estimatedHours: 0 },
      { name: 'Suivi progression', implemented: true, priority: 'high', description: 'Timeline et statuts avancés', estimatedHours: 0 },
      { name: 'Feuille de route', implemented: true, priority: 'high', description: 'Roadmap interactive par phase', estimatedHours: 0 },
      { name: 'Tableau de bord', implemented: true, priority: 'high', description: 'Stats personnalisées + santé projet', estimatedHours: 0 },
      { name: 'Rendez-vous', implemented: true, priority: 'medium', description: 'Planification RDV avec types de sujets', estimatedHours: 0 },
      { name: 'Messagerie', implemented: true, priority: 'medium', description: 'Communication intégrée + templates', estimatedHours: 0 },
      { name: 'Notifications système', implemented: true, priority: 'medium', description: 'Notifications automatiques complètes', estimatedHours: 0 },
      { name: 'Archivage projets', implemented: true, priority: 'medium', description: 'Archiver/restaurer projets', estimatedHours: 0 },
      { name: 'Édition projet', implemented: false, priority: 'medium', description: 'Modifier après soumission', estimatedHours: 16 },
      { name: 'Export PDF feuille route', implemented: false, priority: 'medium', description: 'Export roadmap PDF', estimatedHours: 8 }
    ],
    criticalMissing: [],
    nextPriorities: ['Édition projet après soumission', 'Export PDF avancé', 'Historique modifications']
  },
  {
    name: 'Accompagnateur',
    icon: Users,
    color: 'blue',
    completionRate: 75,
    features: [
      { name: 'Portefeuille projets', implemented: true, priority: 'high', description: 'Vue projets assignés', estimatedHours: 0 },
      { name: 'Validation documents', implemented: true, priority: 'high', description: 'Approuver/rejeter docs individuels', estimatedHours: 0 },
      { name: 'Validation en masse', implemented: true, priority: 'high', description: 'Valider tous documents en attente', estimatedHours: 0 },
      { name: 'Planification RDV', implemented: true, priority: 'high', description: 'Créer RDV avec types de sujets', estimatedHours: 0 },
      { name: 'Calendrier', implemented: true, priority: 'high', description: 'Vue calendrier avec RDV', estimatedHours: 0 },
      { name: 'Messagerie', implemented: true, priority: 'medium', description: 'Communication + templates', estimatedHours: 0 },
      { name: 'Templates messages', implemented: true, priority: 'medium', description: 'Messages prédéfinis par phase roadmap', estimatedHours: 0 },
      { name: 'Stats activité', implemented: true, priority: 'medium', description: 'Dashboard avec KPIs', estimatedHours: 0 },
      { name: 'Alertes projets', implemented: true, priority: 'medium', description: 'Alertes documents/RDV', estimatedHours: 0 },
      { name: 'Rapports activité', implemented: true, priority: 'medium', description: 'Rapports période', estimatedHours: 0 },
      { name: 'Notifications système', implemented: true, priority: 'medium', description: 'Alertes automatiques complètes', estimatedHours: 0 },
      { name: 'Calendrier avancé', implemented: false, priority: 'medium', description: 'Vue semaine/mois + disponibilités', estimatedHours: 16 },
      { name: 'Suivi temps', implemented: false, priority: 'medium', description: 'Temps passé par projet', estimatedHours: 12 }
    ],
    criticalMissing: [],
    nextPriorities: ['Calendrier vue multiple', 'Suivi temps détaillé', 'Analytics avancées']
  },
  {
    name: 'Coordinateur CRESS',
    icon: Crown,
    color: 'purple',
    completionRate: 80,
    features: [
      { name: 'Vue ensemble projets', implemented: true, priority: 'high', description: 'Tous les projets avec filtres', estimatedHours: 0 },
      { name: 'Assignation accompagnateurs', implemented: true, priority: 'high', description: 'Affecter projets', estimatedHours: 0 },
      { name: 'Gestion statuts', implemented: true, priority: 'high', description: 'Changer statuts + historique', estimatedHours: 0 },
      { name: 'Archivage projets', implemented: true, priority: 'high', description: 'Archiver/restaurer projets', estimatedHours: 0 },
      { name: 'Validation documents', implemented: true, priority: 'high', description: 'Vérifier docs tous projets', estimatedHours: 0 },
      { name: 'Validation en masse', implemented: true, priority: 'high', description: 'Valider documents en masse', estimatedHours: 0 },
      { name: 'Dashboard avancé', implemented: true, priority: 'high', description: 'Stats complètes + KPIs', estimatedHours: 0 },
      { name: 'Messagerie globale', implemented: true, priority: 'medium', description: 'Communication tous profils', estimatedHours: 0 },
      { name: 'Notifications système', implemented: true, priority: 'medium', description: 'Alertes automatiques', estimatedHours: 0 },
      { name: 'Gestion accompagnateurs', implemented: true, priority: 'medium', description: 'Valider/suspendre accompagnateurs', estimatedHours: 0 },
      { name: 'Gestion territoires', implemented: true, priority: 'medium', description: 'Territoires prédéfinis', estimatedHours: 0 },
      { name: 'Gestion territoriale avancée', implemented: false, priority: 'medium', description: 'Carte interactive + répartition', estimatedHours: 20 },
      { name: 'Planification ressources', implemented: false, priority: 'medium', description: 'Optimisation charge travail', estimatedHours: 18 }
    ],
    criticalMissing: [],
    nextPriorities: ['Carte géographique interactive', 'Planification automatique', 'Analytics prédictives']
  },
  {
    name: 'Financeur',
    icon: Banknote,
    color: 'orange',
    completionRate: 45,
    features: [
      { name: 'Consultation projets', implemented: true, priority: 'high', description: 'Voir projets éligibles', estimatedHours: 0 },
      { name: 'Documents projets', implemented: true, priority: 'high', description: 'Accès documents validés', estimatedHours: 0 },
      { name: 'Vue financements', implemented: true, priority: 'high', description: 'Financements accordés', estimatedHours: 0 },
      { name: 'Analyse financière', implemented: true, priority: 'high', description: 'Analyse plans financiers IA', estimatedHours: 0 },
      { name: 'Simulation scénarios', implemented: true, priority: 'high', description: 'Simulations financières IA', estimatedHours: 0 },
      { name: 'Stats financement', implemented: true, priority: 'medium', description: 'Statistiques avancées', estimatedHours: 0 },
      { name: 'Interface évaluation', implemented: false, priority: 'high', description: 'Grilles notation projets', estimatedHours: 20 },
      { name: 'Critères configurables', implemented: false, priority: 'high', description: 'Personnaliser critères', estimatedHours: 16 },
      { name: 'Workflow validation', implemented: false, priority: 'high', description: 'Processus approbation', estimatedHours: 18 },
      { name: 'Rapports impact', implemented: false, priority: 'medium', description: 'ROI social et impact', estimatedHours: 14 },
      { name: 'Suivi post-financement', implemented: false, priority: 'medium', description: 'Suivi après accord', estimatedHours: 16 }
    ],
    criticalMissing: ['Interface évaluation', 'Workflow validation'],
    nextPriorities: ['Grilles évaluation', 'Processus validation', 'Rapports impact']
  },
  {
    name: 'Administrateur',
    icon: Settings,
    color: 'red',
    completionRate: 70,
    features: [
      { name: 'Gestion utilisateurs', implemented: true, priority: 'high', description: 'CRUD utilisateurs complet', estimatedHours: 0 },
      { name: 'Création profils', implemented: true, priority: 'high', description: 'Créer tous types profils', estimatedHours: 0 },
      { name: 'Gestion accompagnateurs', implemented: true, priority: 'high', description: 'Validation/suspension accompagnateurs', estimatedHours: 0 },
      { name: 'Gestion porteurs', implemented: true, priority: 'high', description: 'Gestion porteurs de projets', estimatedHours: 0 },
      { name: 'Logs erreurs', implemented: true, priority: 'high', description: 'Monitoring erreurs Sentry', estimatedHours: 0 },
      { name: 'Audit logs', implemented: true, priority: 'high', description: 'Traçabilité complète actions', estimatedHours: 0 },
      { name: 'Dashboard système', implemented: true, priority: 'high', description: 'Métriques système complètes', estimatedHours: 0 },
      { name: 'Config système', implemented: true, priority: 'medium', description: 'Paramètres généraux', estimatedHours: 0 },
      { name: 'Gestion ressources', implemented: true, priority: 'medium', description: 'CMS ressources ESS', estimatedHours: 0 },
      { name: 'Gestion territoires', implemented: true, priority: 'medium', description: 'Configuration territoires', estimatedHours: 0 },
      { name: 'Monitoring temps réel', implemented: false, priority: 'medium', description: 'Dashboard live avancé', estimatedHours: 20 },
      { name: 'Gestion sauvegardes', implemented: false, priority: 'medium', description: 'Backup et restore UI', estimatedHours: 16 },
      { name: 'Audit RGPD', implemented: false, priority: 'medium', description: 'Outils conformité', estimatedHours: 18 }
    ],
    criticalMissing: [],
    nextPriorities: ['Monitoring temps réel avancé', 'Gestion sauvegardes', 'Conformité RGPD']
  }
];

export const ProfileCompletionDashboard: React.FC = () => {
  const [selectedProfile, setSelectedProfile] = useState<ProfileAnalysis | null>(null);

  const getCompletionColor = (rate: number) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-yellow-600';
    if (rate >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getCompletionBg = (rate: number) => {
    if (rate >= 80) return 'bg-green-100 border-green-200';
    if (rate >= 60) return 'bg-yellow-100 border-yellow-200';
    if (rate >= 40) return 'bg-orange-100 border-orange-200';
    return 'bg-red-100 border-red-200';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const totalFeatures = profilesAnalysis.reduce((sum, profile) => sum + profile.features.length, 0);
  const implementedFeatures = profilesAnalysis.reduce((sum, profile) => 
    sum + profile.features.filter(f => f.implemented).length, 0
  );
  const overallCompletion = Math.round((implementedFeatures / totalFeatures) * 100);

  const totalEstimatedHours = profilesAnalysis.reduce((sum, profile) => 
    sum + profile.features.filter(f => !f.implemented).reduce((fSum, f) => fSum + f.estimatedHours, 0), 0
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">Analyse de Complétude par Profil</h1>
        <p className="text-blue-100">
          État des fonctionnalités développées et recommandations d'amélioration
        </p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-sm text-blue-100">Complétude globale</p>
            <p className="text-2xl font-bold">{overallCompletion}%</p>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-sm text-blue-100">Fonctionnalités implémentées</p>
            <p className="text-2xl font-bold">{implementedFeatures}/{totalFeatures}</p>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-sm text-blue-100">Effort restant estimé</p>
            <p className="text-2xl font-bold">{totalEstimatedHours}h</p>
          </div>
        </div>
      </div>

      {/* Profiles Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {profilesAnalysis.map((profile) => {
          const IconComponent = profile.icon;
          const implementedCount = profile.features.filter(f => f.implemented).length;
          const totalCount = profile.features.length;
          
          return (
            <div
              key={profile.name}
              onClick={() => setSelectedProfile(profile)}
              className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105 ${getCompletionBg(profile.completionRate)}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 bg-${profile.color}-600 rounded-full flex items-center justify-center`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{profile.name}</h3>
                    <p className="text-sm text-gray-600">{implementedCount}/{totalCount} fonctionnalités</p>
                  </div>
                </div>
                <div className={`text-2xl font-bold ${getCompletionColor(profile.completionRate)}`}>
                  {profile.completionRate}%
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`bg-${profile.color}-600 h-3 rounded-full transition-all duration-500`}
                    style={{ width: `${profile.completionRate}%` }}
                  />
                </div>
              </div>

              {/* Critical Missing */}
              {profile.criticalMissing.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-red-800">Manques critiques :</p>
                  {profile.criticalMissing.slice(0, 2).map((missing, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      <span className="text-sm text-red-700">{missing}</span>
                    </div>
                  ))}
                  {profile.criticalMissing.length > 2 && (
                    <p className="text-xs text-red-600">+{profile.criticalMissing.length - 2} autres...</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Detailed View */}
      {selectedProfile && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <selectedProfile.icon className={`w-8 h-8 text-${selectedProfile.color}-600`} />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{selectedProfile.name}</h2>
                <p className="text-gray-600">Analyse détaillée des fonctionnalités</p>
              </div>
            </div>
            <button
              onClick={() => setSelectedProfile(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Features List */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Fonctionnalités ({selectedProfile.features.length})</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {selectedProfile.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                    {feature.implemented ? (
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <p className="font-medium text-gray-900">{feature.name}</p>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(feature.priority)}`}>
                          {feature.priority}
                        </span>
                        {!feature.implemented && (
                          <span className="text-xs text-gray-500">~{feature.estimatedHours}h</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Priorities */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Prochaines Priorités</h3>
              <div className="space-y-3">
                {selectedProfile.nextPriorities.map((priority, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {index + 1}
                    </div>
                    <span className="font-medium text-blue-900">{priority}</span>
                  </div>
                ))}
              </div>

              {/* Effort Estimation */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Estimation d'effort</h4>
                <div className="space-y-2">
                  {['high', 'medium', 'low'].map(priority => {
                    const features = selectedProfile.features.filter(f => !f.implemented && f.priority === priority);
                    const hours = features.reduce((sum, f) => sum + f.estimatedHours, 0);
                    
                    return (
                      <div key={priority} className="flex justify-between text-sm">
                        <span className="capitalize">{priority} priority:</span>
                        <span className="font-medium">{hours}h ({features.length} fonctionnalités)</span>
                      </div>
                    );
                  })}
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="flex justify-between font-medium">
                      <span>Total estimé:</span>
                      <span>{selectedProfile.features.filter(f => !f.implemented).reduce((sum, f) => sum + f.estimatedHours, 0)}h</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Global Recommendations */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">🎯 Recommandations Globales</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-gray-900 mb-3">🔥 Priorités Critiques</h3>
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                <span>Développer interface évaluation (Financeurs) - 20h</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                <span>Workflow validation financements (Financeurs) - 18h</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                <span>Calendrier vue multiple (Accompagnateurs) - 16h</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                <span>Carte territoriale interactive (Coordinateurs) - 20h</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-3">📈 Optimisations</h3>
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                <span>Édition projets après soumission (Porteurs) - 16h</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <BarChart3 className="w-4 h-4 text-blue-600" />
                <span>Suivi temps par projet (Accompagnateurs) - 12h</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span>Intégrations calendrier externes - 12h</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <FileText className="w-4 h-4 text-blue-600" />
                <span>Exports PDF avancés et rapports - 14h</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Implementation Timeline */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">📅 Timeline d'Implémentation Recommandée</h2>

        <div className="space-y-4">
          {[
            { week: 1, title: 'Financeurs - Évaluation', items: ['Interface évaluation', 'Critères configurables', 'Workflow'], color: 'orange', hours: 54 },
            { week: 2, title: 'Accompagnateurs - Calendrier', items: ['Vue multiple', 'Disponibilités', 'Sync externe'], color: 'blue', hours: 28 },
            { week: 3, title: 'Coordinateurs - Territoire', items: ['Carte interactive', 'Répartition géo', 'Optimisation'], color: 'purple', hours: 38 },
            { week: 4, title: 'Porteurs - Édition', items: ['Édition projets', 'Export PDF', 'Historique'], color: 'green', hours: 24 },
            { week: 5, title: 'Financeurs - Suivi', items: ['Rapports impact', 'Suivi post-financement', 'Analytics'], color: 'orange', hours: 30 },
            { week: 6, title: 'Admin - Système', items: ['Monitoring live', 'Sauvegardes', 'RGPD'], color: 'red', hours: 54 }
          ].map((phase) => (
            <div key={phase.week} className="flex items-center space-x-4">
              <div className={`w-12 h-12 bg-${phase.color}-600 rounded-full flex items-center justify-center text-white font-bold`}>
                S{phase.week}
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">{phase.title}</h3>
                <p className="text-sm text-gray-600">{phase.items.join(' • ')}</p>
              </div>
              <div className="text-sm text-gray-500 font-medium">
                ~{phase.hours}h
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};